// ============================================================================
// GENERIC DOCUMENT ANALYZER TYPES
// ============================================================================

import type { FacetTypeDefinition } from './facetSchema'
import {
  Mic,
  Briefcase,
  Users,
  UsersRound,
  Target,
  ClipboardList,
  Presentation,
  FlaskConical,
  Swords,
  Ticket,
  ClipboardCheck,
  File,
  type LucideIcon,
} from 'lucide-react'

// Map icon names to Lucide components for CONTENT_TYPES
export const CONTENT_TYPE_ICONS: Record<string, LucideIcon> = {
  Mic,
  Briefcase,
  Users,
  UsersRound,
  Target,
  ClipboardList,
  Presentation,
  FlaskConical,
  Swords,
  Ticket,
  ClipboardCheck,
  File,
}

/** Get the Lucide icon component for a content type */
export function getContentTypeIcon(iconName: string): LucideIcon {
  return CONTENT_TYPE_ICONS[iconName] || File
}

// ============================================================================
// CONTENT TYPES
// ============================================================================

export type ContentType =
  | 'user_interview'        // End user feedback
  | 'buyer_interview'       // Buyer/decision maker interview
  | 'stakeholder_meeting'   // Internal stakeholder
  | 'team_meeting'          // Team discussion notes
  | 'vision_document'       // Vision/strategy doc
  | 'requirements_spec'     // Requirements document
  | 'presentation'          // Slides/pitch deck
  | 'research_notes'        // Research findings
  | 'competitor_analysis'   // Competitive analysis
  | 'support_ticket'        // Customer support data
  | 'survey_response'       // Survey results
  | 'custom'                // User-defined

export interface ContentTypeInfo {
  id: ContentType
  name: string
  description: string
  icon: string
  aiHints: string  // Hints for AI analysis
}

export const CONTENT_TYPES: ContentTypeInfo[] = [
  {
    id: 'user_interview',
    name: 'User Interview',
    description: 'End user feedback and interview transcripts',
    icon: 'Mic',
    aiHints: `Focus on: problems, pains, gains, jobs, workarounds, feature requests.
Look for emotional language, frustrations, and unmet needs.
Pay attention to quotes that reveal deep insights about user behavior.`,
  },
  {
    id: 'buyer_interview',
    name: 'Buyer Interview',
    description: 'Buyer or decision maker interview',
    icon: 'Briefcase',
    aiHints: `Focus on: decision criteria, budget constraints, competitors considered,
value propositions, risks, and stakeholder dynamics.
Look for buying signals and objections.`,
  },
  {
    id: 'stakeholder_meeting',
    name: 'Stakeholder Meeting',
    description: 'Internal stakeholder meeting notes',
    icon: 'Users',
    aiHints: `Focus on: decisions, action items, dependencies, risks,
strategic goals, and cross-team alignment issues.
Look for organizational constraints and priorities.`,
  },
  {
    id: 'team_meeting',
    name: 'Team Meeting',
    description: 'Team discussion and planning notes',
    icon: 'UsersRound',
    aiHints: `Focus on: action items, decisions, dependencies, risks,
features discussed, and timeline commitments.
Look for technical constraints and team concerns.`,
  },
  {
    id: 'vision_document',
    name: 'Vision Document',
    description: 'Vision, strategy, or roadmap document',
    icon: 'Target',
    aiHints: `Focus on: strategic goals, value propositions, target segments,
opportunities, competitive positioning, and success metrics.
Look for the "why" behind product decisions.`,
  },
  {
    id: 'requirements_spec',
    name: 'Requirements Spec',
    description: 'Requirements or specification document',
    icon: 'ClipboardList',
    aiHints: `Focus on: requirements, constraints, user stories, dependencies,
acceptance criteria, and quality attributes.
Look for must-haves vs nice-to-haves.`,
  },
  {
    id: 'presentation',
    name: 'Presentation',
    description: 'Slides, pitch deck, or presentation notes',
    icon: 'Presentation',
    aiHints: `Focus on: key messages, value propositions, target audience,
competitive differentiation, and call-to-actions.
Look for the main narrative and supporting points.`,
  },
  {
    id: 'research_notes',
    name: 'Research Notes',
    description: 'Market research or user research findings',
    icon: 'FlaskConical',
    aiHints: `Focus on: insights, patterns, trends, personas, segments,
and validated/invalidated hypotheses.
Look for surprising findings and data points.`,
  },
  {
    id: 'competitor_analysis',
    name: 'Competitor Analysis',
    description: 'Competitive analysis or market landscape',
    icon: 'Swords',
    aiHints: `Focus on: competitors, alternatives, trends, threats,
strengths, weaknesses, and market positioning.
Look for competitive gaps and opportunities.`,
  },
  {
    id: 'support_ticket',
    name: 'Support Ticket',
    description: 'Customer support data or feedback',
    icon: 'Ticket',
    aiHints: `Focus on: problems, bugs, feature requests, workarounds,
and customer frustrations.
Look for recurring issues and severity patterns.`,
  },
  {
    id: 'survey_response',
    name: 'Survey Response',
    description: 'Survey results or feedback forms',
    icon: 'ClipboardCheck',
    aiHints: `Focus on: quantitative patterns, qualitative feedback,
satisfaction indicators, and improvement suggestions.
Look for themes across multiple responses.`,
  },
  {
    id: 'custom',
    name: 'Custom',
    description: 'Other document type',
    icon: 'File',
    aiHints: `Analyze the content comprehensively, looking for:
problems, opportunities, decisions, stakeholders, features,
requirements, risks, and any other relevant insights.`,
  },
]

export const CONTENT_TYPE_MAP = new Map<ContentType, ContentTypeInfo>(
  CONTENT_TYPES.map(ct => [ct.id, ct])
)

// ============================================================================
// SOURCE TYPES
// ============================================================================

export type SourceStatus = 'pending' | 'analyzing' | 'analyzed' | 'error'

export interface Source {
  id: string
  projectId: string
  accountId: string
  createdById: string
  title: string
  contentType: ContentType
  rawContent: string
  markdownContent: string
  fileType?: string
  fileName?: string
  status: SourceStatus
  analyzedAt?: Date
  analysisError?: string
  metadata?: Record<string, unknown>
  tags?: string[]
  createdAt: Date
  updatedAt: Date
}

// ============================================================================
// DETECTED FACET TYPES
// ============================================================================

export type FacetStatus = 'pending' | 'accepted' | 'rejected' | 'mapped'

/**
 * Structured problem statement using the "Three C's" framework
 * Only used for problem-type facets (problem, customer_pain, workaround, current_behavior)
 */
export interface ProblemStatement {
  context: string      // "When... [specific situation]"
  cause: string        // "This can cause... [negative impact]"
  consequence: string  // "This is problematic because... [impact]"
}

export interface DetectedFacet {
  id: string
  sourceId: string
  projectId: string
  facetTypeId: string
  title?: string          // Short descriptive name (5-10 words)
  content: string         // Summary (1-2 sentences)
  evidence: string
  confidence: number
  problemStatement?: ProblemStatement  // Three C's for problem-type facets
  positionStart?: number
  positionEnd?: number
  suggestedEntityType?: string
  suggestedReason?: string
  status: FacetStatus
  mappedEntityType?: string
  mappedEntityUri?: string
  decidedAt?: Date
  decidedById?: string
  createdAt: Date
}

export interface DetectedFacetWithType extends DetectedFacet {
  facetType: FacetTypeDefinition | undefined
}

// ============================================================================
// ANALYSIS TYPES
// ============================================================================

export interface AnalysisRequest {
  sourceId: string
  content: string
  contentType: ContentType
  enabledFacetIds?: string[]  // If not provided, use all enabled facets
}

/**
 * Extracted entity from content (for knowledge graph)
 */
export interface ExtractedEntity {
  id: string                  // Unique ID like "company_1", "segment_1"
  type: string                // Entity type: Company, CustomerSegment, Problem, etc.
  name: string                // The actual name: "Sferio", "Mid-market CFOs"
  description: string         // Brief description
  evidence: string            // Supporting quote from content
  confidence: number          // 0-1
}

/**
 * Extracted relationship between entities (for knowledge graph)
 */
export interface ExtractedRelationship {
  from: string                // Source entity ID
  to: string                  // Target entity ID
  type: string                // Relationship type: targetsSegment, hasPain, etc.
  evidence: string            // Supporting text
  confidence: number          // 0-1
}

export interface AnalysisResult {
  sourceId: string
  facets: DetectedFacetResult[]
  entities?: ExtractedEntity[]           // Extracted named entities
  relationships?: ExtractedRelationship[] // Relationships between entities
  analysisTime: number  // ms
  tokenCount?: number
  error?: string  // Error message if analysis failed
}

export interface DetectedFacetResult {
  facetTypeId: string
  title?: string          // Short descriptive name (5-10 words)
  content: string         // Summary (1-2 sentences)
  evidence: string
  confidence: number
  problemStatement?: ProblemStatement  // Three C's for problem-type facets
  positionStart?: number
  positionEnd?: number
  suggestedEntityType?: string
  suggestedReason?: string
}

// ============================================================================
// FACET CONFIGURATION TYPES
// ============================================================================

export interface FacetConfiguration {
  id: string
  projectId: string
  facetTypeId: string
  isEnabled: boolean
  customEntityType?: string  // Override default mapping
  updatedAt: Date
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface SourceWithFacets extends Source {
  facets: DetectedFacetWithType[]
  facetStats: {
    total: number
    pending: number
    accepted: number
    rejected: number
    mapped: number
  }
}

export interface FacetDecision {
  facetId: string
  action: 'accept' | 'reject'
  entityType?: string  // Override for custom mapping
}

export interface BulkFacetDecision {
  facetIds: string[]
  action: 'accept' | 'reject'
}

export interface EntityCreationResult {
  facetId: string
  entityUri: string
  entityType: string
  success: boolean
  error?: string
}
